import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellOff, CheckCheck } from 'lucide-react'
import {
  useGetNotificationPreferencesQuery,
  useListNotificationsQuery,
  useMarkNotificationsReadMutation
} from '@axilon-apps/domains-ontology-ui-state'
import {
  setBrowserNotificationsPreference,
  useBrowserNotificationsPreference
} from '../../lib/browser-notifications-preference'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'
import { Empty } from '../primitives/Empty'
import { NotificationItem } from './NotificationItem'
import {
  requestBrowserNotificationPermission,
  useBrowserNotificationPermission
} from './browser-notify'
import { type NotificationBucket, presentNotifications } from './notification-presentation'
import { useNotificationActions } from './useNotificationActions'

const POLLING_INTERVAL_MS = 30_000

/**
 * Bell popover: "Needs you" (default; drives the badge) and "Activity"
 * (completions and receipts; never counted). Each row opens its subject and
 * offers the producer's inline actions.
 */
export function NotificationsPopover({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate()
  const [tab, setTab] = useState<NotificationBucket>('action')
  const [forYou, setForYou] = useState(false)
  const { data: prefs } = useGetNotificationPreferencesQuery()
  const showActivity = prefs?.show_activity !== false
  const { data, isLoading } = useListNotificationsQuery(
    { state: 'open', ...(forYou ? { for_you: true } : {}) },
    { pollingInterval: POLLING_INTERVAL_MS }
  )
  const items = useMemo(() => presentNotifications(data ?? []), [data])
  const needsYou = items.filter((i) => i.presentation.bucket === 'action')
  const activity = showActivity ? items.filter((i) => i.presentation.bucket === 'activity') : []
  const activeTab = showActivity ? tab : 'action'
  const visible = activeTab === 'action' ? needsYou : activity
  const { open, run } = useNotificationActions(onClose)
  const [markRead] = useMarkNotificationsReadMutation()

  // Reading the bell marks what was on screen read once it closes, so the
  // unread dots survive the glance that opened it.
  const seen = useRef(new Set<string>())
  useEffect(() => {
    for (const item of visible)
      for (const m of item.members) if (m.read === false) seen.current.add(m.id)
  }, [visible])
  useEffect(
    () => () => {
      const ids = [...seen.current]
      if (ids.length) void markRead({ ids })
    },
    [markRead]
  )

  const clearActivity = useCallback(() => {
    void markRead({ all: true, bucket: 'activity' })
  }, [markRead])

  const desktopPermission = useBrowserNotificationPermission()
  const desktopPreference = useBrowserNotificationsPreference()
  const showEnableDesktop =
    desktopPermission === 'default' || (desktopPermission === 'granted' && !desktopPreference)

  const handleEnableDesktop = async () => {
    if (desktopPermission === 'default') {
      const next = await requestBrowserNotificationPermission()
      if (next === 'granted') setBrowserNotificationsPreference(true)
    } else if (desktopPermission === 'granted') {
      setBrowserNotificationsPreference(true)
    }
  }

  const tabButton = (value: NotificationBucket, label: string, count: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={activeTab === value}
      onClick={() => setTab(value)}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-12 font-medium text-ink-2',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        activeTab === value && 'bg-accent text-ink'
      )}
    >
      {label}
      {count > 0 ? (
        <span
          className={cn(
            'min-w-4 rounded-full px-1 text-center font-mono text-10 leading-4',
            value === 'action' ? 'bg-st-down text-white' : 'bg-line-2 text-ink-2'
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  )

  return (
    <>
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2">
        <span className="text-sm font-medium">Notifications</span>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            aria-pressed={forYou}
            onClick={() => setForYou((v) => !v)}
            className={cn(
              'mr-1 cursor-pointer rounded-md border border-line px-1.5 py-0.5 text-11 text-ink-2 hover:bg-accent',
              forYou && 'border-st-info bg-st-info-bg text-st-info'
            )}
          >
            For you
          </button>
          <div
            className="flex items-center gap-0.5"
            role="tablist"
            aria-label="Notification buckets"
          >
            {tabButton('action', 'Needs you', needsYou.length)}
            {showActivity
              ? tabButton(
                  'activity',
                  'Activity',
                  activity.filter((i) => i.members.some((m) => m.read === false)).length
                )
              : null}
          </div>
        </div>
      </div>
      <Separator />
      <div className="min-h-0 max-h-130 overflow-y-auto overscroll-contain">
        {isLoading && items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            Loading notifications…
          </div>
        ) : visible.length === 0 ? (
          <Empty
            icon={activeTab === 'action' ? CheckCheck : BellOff}
            title={
              activeTab === 'action'
                ? forYou
                  ? 'Nothing addressed to you'
                  : 'Nothing needs you'
                : 'No recent activity'
            }
            hint={
              activeTab === 'action'
                ? 'Findings, failures and requests that need a decision show up here.'
                : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((item) => (
              <li key={item.presentation.groupKey}>
                <NotificationItem item={item} onOpen={open} onAction={run} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <Separator />
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-1.5">
        {activeTab === 'activity' && activity.length > 0 ? (
          <Button type="button" size="xs" variant="link" onClick={clearActivity}>
            Mark all read
          </Button>
        ) : showEnableDesktop ? (
          <Button type="button" size="xs" variant="link" onClick={() => void handleEnableDesktop()}>
            {desktopPermission === 'granted' ? 'Turn on desktop alerts' : 'Enable desktop alerts'}
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          size="xs"
          variant="link"
          onClick={() => {
            onClose?.()
            navigate('/notifications')
          }}
        >
          See all
        </Button>
      </div>
    </>
  )
}
